import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  concatMap,
  debounceTime,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  timer,
} from 'rxjs';

import * as fromGenerated from '../../_generated';
import {
  AdminTeammateInvitee,
  AdminTeammatesActions,
} from './admin-teammates.actions';

/**
 * Effects for the Admin Teammates surface. Reads: list + lazy dropdown payload.
 * Builder data: users + groups (on open). Writes: create (atomic skill-attach),
 * content update, delete, and the invitee grant/revoke fan-out — each leaf is one
 * endpoint with its own per-entity failure toast (no shared error path).
 */
@Injectable()
export class AdminTeammatesEffects {
  private readonly actions$ = inject(Actions);
  private readonly adminService = inject(fromGenerated.AdminService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  // ─── Reads ─────────────────────────────────────────────────────────────────
  loadTeammates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.loadTeammates),
      switchMap(() =>
        forkJoin([
          this.adminService.adminTeammatesControllerListV1(
            undefined,
            undefined,
            undefined,
            this.currentLocale(),
          ),
          timer(1000),
        ]).pipe(
          map(([teammates]) =>
            AdminTeammatesActions.loadTeammatesSuccess({ teammates }),
          ),
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.loadTeammatesFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadTeammatesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.loadTeammatesFailure),
        tap(() => this.toastError('ADMIN.TEAMMATES.TOAST.LOAD_ERROR')),
      ),
    { dispatch: false },
  );

  loadDropdownPayload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.loadDropdownPayload),
      concatMap(({ teammateId }) =>
        this.adminService
          .adminTeammatesControllerGetDropdownPayloadV1(
            teammateId,
            this.currentLocale(),
          )
          .pipe(
            map((payload) =>
              AdminTeammatesActions.loadDropdownPayloadSuccess({
                teammateId,
                payload,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.loadDropdownPayloadFailure({
                  teammateId,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadDropdownPayloadFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.loadDropdownPayloadFailure),
        tap(() =>
          this.toastError('ADMIN.TEAMMATES.TOAST.DROPDOWN_PAYLOAD_ERROR'),
        ),
      ),
    { dispatch: false },
  );

  // ─── Builder data (fired when the Builder opens) ─────────────────────────────
  loadBuilderData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AdminTeammatesActions.openCreateModal,
        AdminTeammatesActions.openEditModal,
      ),
      mergeMap(() => [
        AdminTeammatesActions.loadAllUsers(),
        AdminTeammatesActions.loadAllGroups(),
      ]),
    ),
  );

  loadAllUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.loadAllUsers),
      switchMap(() =>
        this.adminService.adminControllerGetUsersV1().pipe(
          map((users) => AdminTeammatesActions.loadAllUsersSuccess({ users })),
          // A fetch error must stay distinct from "no users exist" — mapping it
          // to an empty success would hide the failure and leave the invite
          // typeahead silently empty.
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.loadAllUsersFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadAllUsersFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.loadAllUsersFailure),
        tap(() => this.toastError('ADMIN.TEAMMATES.TOAST.LOAD_USERS_ERROR')),
      ),
    { dispatch: false },
  );

  loadAllGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.loadAllGroups),
      switchMap(() =>
        this.adminService.adminGroupsControllerListV1().pipe(
          map((groups) =>
            AdminTeammatesActions.loadAllGroupsSuccess({ groups }),
          ),
          // Keep a failure distinct from "no groups exist": an empty success
          // would leave allGroupId null and the org-wide grant path would
          // silently no-op with nothing surfaced to the user.
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.loadAllGroupsFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadAllGroupsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.loadAllGroupsFailure),
        tap(() => this.toastError('ADMIN.TEAMMATES.TOAST.LOAD_GROUPS_ERROR')),
      ),
    { dispatch: false },
  );

  // Edit open: seed staged invitees (from the access graph) + attached skills
  // (from the dropdown payload). One forkJoin, two seed actions.
  openEditModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.openEditModal),
      mergeMap(({ teammate }) =>
        forkJoin([
          this.adminService.adminAccessControllerGetAccessByTeammateV1(
            teammate.id,
          ),
          this.adminService.adminTeammatesControllerGetDropdownPayloadV1(
            teammate.id,
            this.currentLocale(),
          ),
        ]).pipe(
          mergeMap(([access, payload]) => {
            const invitees: AdminTeammateInvitee[] = [
              ...access.users.map((u) => ({
                kind: 'user' as const,
                id: u.id,
                displayName: u.displayName,
                secondary: u.email,
              })),
              ...access.groups.map((g) => ({
                kind: 'group' as const,
                id: g.id,
                displayName: g.displayName,
              })),
            ];
            return [
              AdminTeammatesActions.seedEditInvitees({ invitees }),
              AdminTeammatesActions.seedEditSkills({
                skillIds: payload.skills.items.map((s) => s.id),
              }),
              // Cache the payload so the Builder can resolve attached-skill names
              // and the row's count cells expand without a second fetch.
              AdminTeammatesActions.loadDropdownPayloadSuccess({
                teammateId: teammate.id,
                payload,
              }),
            ];
          }),
          // A failed seed must not leave the Builder open showing an empty
          // editor for a teammate that actually has access/skills (and risking a
          // diff-based save against the wrong baseline): surface the error and
          // close the Builder so the admin reopens once the fetch recovers.
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.loadDropdownPayloadFailure({
                teammateId: teammate.id,
                error: error.message,
              }),
              AdminTeammatesActions.closeModal(),
            ),
          ),
        ),
      ),
    ),
  );

  // ─── Skills typeahead (debounced, cancel-on-newer) ───────────────────────────
  searchSkills$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.searchSkills),
      debounceTime(250),
      switchMap(({ query }) =>
        this.adminService
          .adminSkillsControllerListV1(query, undefined, this.currentLocale())
          .pipe(
            map((results) =>
              AdminTeammatesActions.searchSkillsSuccess({ results }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.searchSkillsFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // ─── Create (teammate + skills atomic; invitees fan out after) ───────────────
  createTeammate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.createTeammate),
      switchMap(({ body, accessMode, invitees, allGroupId }) =>
        this.adminService.adminTeammatesControllerCreateShareableV1(body).pipe(
          map((teammate) =>
            AdminTeammatesActions.createTeammateSuccess({
              teammate,
              accessMode,
              invitees,
              allGroupId,
            }),
          ),
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.createTeammateFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // Pure dispatcher: one grant per staged invitee (or one Group(all) for org-wide).
  afterCreateTeammateFanOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.createTeammateSuccess),
      mergeMap(({ teammate, accessMode, invitees, allGroupId }) => {
        if (accessMode === 'org-wide') {
          if (!allGroupId) {
            return [];
          }
          return [
            AdminTeammatesActions.grantGroupTeammateAccess({
              teammateId: teammate.id,
              groupId: allGroupId,
              groupDisplayName: this.orgWideLabel(),
            }),
          ];
        }
        return invitees.map((invitee) => this.grantFor(teammate.id, invitee));
      }),
    ),
  );

  createTeammateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.createTeammateSuccess),
        tap(() => this.toastSuccess('ADMIN.TEAMMATES.TOAST.CREATE_SUCCESS')),
      ),
    { dispatch: false },
  );

  createTeammateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.createTeammateFailure),
        tap(() => this.toastError('ADMIN.TEAMMATES.TOAST.CREATE_ERROR')),
      ),
    { dispatch: false },
  );

  // ─── Update content (super_admin) ────────────────────────────────────────────
  updateTeammateContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.updateTeammateContent),
      switchMap(({ teammateId, body }) =>
        this.adminService
          .adminTeammatesControllerUpdateV1(teammateId, body)
          .pipe(
            map((teammate) =>
              AdminTeammatesActions.updateTeammateContentSuccess({ teammate }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.updateTeammateContentFailure({
                  teammateId,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateTeammateContentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.updateTeammateContentSuccess),
        tap(() =>
          this.toastSuccess('ADMIN.TEAMMATES.TOAST.UPDATE_CONTENT_SUCCESS'),
        ),
      ),
    { dispatch: false },
  );

  updateTeammateContentFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.updateTeammateContentFailure),
        tap(() =>
          this.toastError('ADMIN.TEAMMATES.TOAST.UPDATE_CONTENT_ERROR'),
        ),
      ),
    { dispatch: false },
  );

  // ─── Delete ──────────────────────────────────────────────────────────────────
  deleteTeammate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.deleteTeammate),
      switchMap(({ teammateId }) =>
        this.adminService.adminTeammatesControllerDeleteV1(teammateId).pipe(
          map(() =>
            AdminTeammatesActions.deleteTeammateSuccess({ teammateId }),
          ),
          catchError((error: Error) =>
            of(
              AdminTeammatesActions.deleteTeammateFailure({
                teammateId,
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  deleteTeammateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.deleteTeammateSuccess),
        tap(() => this.toastSuccess('ADMIN.TEAMMATES.TOAST.DELETE_SUCCESS')),
      ),
    { dispatch: false },
  );

  deleteTeammateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminTeammatesActions.deleteTeammateFailure),
        tap(() => this.toastError('ADMIN.TEAMMATES.TOAST.DELETE_ERROR')),
      ),
    { dispatch: false },
  );

  // ─── Invitee fan-out (pure dispatcher: one leaf per diff entry) ──────────────
  fanOutInviteeDiff$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.fanOutInviteeDiff),
      mergeMap(({ teammateId, adds, removes }) => [
        ...adds.map((invitee) => this.grantFor(teammateId, invitee)),
        ...removes.map((invitee) => this.revokeFor(teammateId, invitee)),
      ]),
    ),
  );

  grantUserTeammateAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.grantUserTeammateAccess),
      mergeMap(({ teammateId, userId, userDisplayName }) =>
        this.adminService
          .adminAccessControllerGrantUserToTeammateV1({ userId, teammateId })
          .pipe(
            map(() =>
              AdminTeammatesActions.grantUserTeammateAccessSuccess({
                teammateId,
                userId,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.grantUserTeammateAccessFailure({
                  teammateId,
                  userId,
                  userDisplayName,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  revokeUserTeammateAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.revokeUserTeammateAccess),
      mergeMap(({ teammateId, userId, userDisplayName }) =>
        this.adminService
          .adminAccessControllerRevokeUserFromTeammateV1(userId, teammateId)
          .pipe(
            map(() =>
              AdminTeammatesActions.revokeUserTeammateAccessSuccess({
                teammateId,
                userId,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.revokeUserTeammateAccessFailure({
                  teammateId,
                  userId,
                  userDisplayName,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  grantGroupTeammateAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.grantGroupTeammateAccess),
      mergeMap(({ teammateId, groupId, groupDisplayName }) =>
        this.adminService
          .adminAccessControllerGrantGroupToTeammateV1({ groupId, teammateId })
          .pipe(
            map(() =>
              AdminTeammatesActions.grantGroupTeammateAccessSuccess({
                teammateId,
                groupId,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.grantGroupTeammateAccessFailure({
                  teammateId,
                  groupId,
                  groupDisplayName,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  revokeGroupTeammateAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminTeammatesActions.revokeGroupTeammateAccess),
      mergeMap(({ teammateId, groupId, groupDisplayName }) =>
        this.adminService
          .adminAccessControllerRevokeGroupFromTeammateV1(groupId, teammateId)
          .pipe(
            map(() =>
              AdminTeammatesActions.revokeGroupTeammateAccessSuccess({
                teammateId,
                groupId,
              }),
            ),
            catchError((error: Error) =>
              of(
                AdminTeammatesActions.revokeGroupTeammateAccessFailure({
                  teammateId,
                  groupId,
                  groupDisplayName,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // ─── Per-entity failure toasts (name the failed user/group) ──────────────────
  grantUserFailure$ = this.failureToast(
    AdminTeammatesActions.grantUserTeammateAccessFailure,
    'ADMIN.TEAMMATES.TOAST.GRANT_USER_ACCESS_ERROR',
    'user',
  );
  revokeUserFailure$ = this.failureToast(
    AdminTeammatesActions.revokeUserTeammateAccessFailure,
    'ADMIN.TEAMMATES.TOAST.REVOKE_USER_ACCESS_ERROR',
    'user',
  );
  grantGroupFailure$ = this.failureToast(
    AdminTeammatesActions.grantGroupTeammateAccessFailure,
    'ADMIN.TEAMMATES.TOAST.GRANT_GROUP_ACCESS_ERROR',
    'group',
  );
  revokeGroupFailure$ = this.failureToast(
    AdminTeammatesActions.revokeGroupTeammateAccessFailure,
    'ADMIN.TEAMMATES.TOAST.REVOKE_GROUP_ACCESS_ERROR',
    'group',
  );

  private grantFor(teammateId: string, invitee: AdminTeammateInvitee): Action {
    return invitee.kind === 'user'
      ? AdminTeammatesActions.grantUserTeammateAccess({
          teammateId,
          userId: invitee.id,
          userDisplayName: invitee.displayName,
        })
      : AdminTeammatesActions.grantGroupTeammateAccess({
          teammateId,
          groupId: invitee.id,
          groupDisplayName: invitee.displayName,
        });
  }

  private revokeFor(teammateId: string, invitee: AdminTeammateInvitee): Action {
    return invitee.kind === 'user'
      ? AdminTeammatesActions.revokeUserTeammateAccess({
          teammateId,
          userId: invitee.id,
          userDisplayName: invitee.displayName,
        })
      : AdminTeammatesActions.revokeGroupTeammateAccess({
          teammateId,
          groupId: invitee.id,
          groupDisplayName: invitee.displayName,
        });
  }

  private orgWideLabel(): string {
    return this.translateService.instant(
      'ADMIN.TEAMMATES.TABLE.USERS_ORG_WIDE',
    ) as string;
  }

  /**
   * Admin reads resolve localized teammate/skill names server-side, so every
   * request that accepts a locale gets the viewer's current UI language.
   */
  private currentLocale(): 'en' | 'fr' {
    // The app registers only en + fr; narrow at runtime (default en) rather than
    // casting, so an unexpected lang can't reach the API as an invalid locale.
    return this.translateService.getCurrentLang() === 'fr' ? 'fr' : 'en';
  }

  /** Build a dispatch:false toast effect that names the failed entity. */
  private failureToast(
    action: { type: string },
    key: string,
    interpolationKey: 'user' | 'group',
  ) {
    return createEffect(
      () =>
        this.actions$.pipe(
          ofType(action as never),
          tap(
            (payload: {
              userDisplayName?: string;
              groupDisplayName?: string;
            }) =>
              this.toastrService.error(
                this.translateService.instant(key, {
                  [interpolationKey]:
                    payload.userDisplayName ?? payload.groupDisplayName ?? '',
                }) as string,
                undefined,
                { closeButton: true },
              ),
          ),
        ),
      { dispatch: false },
    );
  }

  private toastSuccess(key: string): void {
    this.toastrService.success(
      this.translateService.instant(key) as string,
      undefined,
      { closeButton: true },
    );
  }

  private toastError(key: string): void {
    this.toastrService.error(
      this.translateService.instant(key) as string,
      undefined,
      { closeButton: true },
    );
  }
}
